import { Section, SectionParagraph as Subtitle, SectionTitle as Title } from '@/components/shared/Content';
import { useSectionEventOnViewportEntrance } from '@/hooks/useSectionEventOnViewportEntrance';
import { Box, Flex, useLottie } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import parse from 'html-react-parser';
import React, { useRef } from 'react';
import styled from 'styled-components';
import lowMilageLottieData from './assets/low_milage-lottie.json';

const Connector = styled(Box)`
  position: absolute;
  left: 0;
  width: 200%;
  transform-origin: top left;
  background: #f7f7f7;
  z-index: 1;
`;

const TopConnector = styled(Connector)`
  height: 150px;
  transform: translateY(-200px) rotate(3deg);

  @media (min-width: 1920px) {
    height: 150px;
    transform: translateY(-210px) rotate(3deg);
  }

  @media (min-width: 2400px) {
    height: 180px;
    transform: translateY(-250px) rotate(2deg);
  }

  ${MEDIA_QUERIES.tablet`
        height: 160px;
        transform: translateY(-190px) rotate(3deg);
    `}

  ${MEDIA_QUERIES.mobile`
        height: 70px;
        transform: translateY(-110px) rotate(3deg);
    `}
`;

const BottomConnector = styled(Connector)`
  height: 100px;
  transform: translateY(850px);

  ${MEDIA_QUERIES.tablet`
        height: 100px;
        transform: translateY(850px);
    `}

  ${MEDIA_QUERIES.mobile`
        height: 50px;
        transform: translateY(770px);
    `}
`;

const Titles = styled(Flex)`
  transform: translateY(80px);

  ${MEDIA_QUERIES.tablet`
        transform: translateY(80px);
    `}

  ${MEDIA_QUERIES.mobile`
        transform: translateY(60px);
    `}
`;

const Lottie = styled(Box)`
  ${MEDIA_QUERIES.mobile`
        transform: translate(45px);
    `}
`;

export interface LowMilageProps {
  title: string;
  subtitle: string;
}

const LottieSection = () => {
  const lottieRef = useRef<HTMLDivElement>(null);

  useLottie(lottieRef, {
    animationData: lowMilageLottieData,
    lottieConfig: {
      loop: true,
      renderer: 'svg',
    },
    scrollOptions: {
      pauseOnScroll: false,
      playTrigger: {
        threshold: 0,
        condition: 'intersect',
        pauseIfConditionsUnmet: true,
      },
    },
  });

  return (
    <Box height={[795, 1045]} mt={[-185, -215]} overflow="hidden">
      <Lottie width={[1246, 1676]} ref={lottieRef} />
    </Box>
  );
};

const LowMilage: React.FC<LowMilageProps> = ({ title, subtitle }) => {
  const intersectionRef = useSectionEventOnViewportEntrance({
    sectionName: 'lowMilage',
    page: 'car',
    position: 5,
  });
  return (
    <Section
      mt={-90}
      mb={0}
      pt={[145, 180, 180, 180]}
      pb={0}
      width="100%"
      position="relative"
      aria-label="auto insurance based on how much you drive"
      contain="paint"
      ref={intersectionRef}
    >
      <TopConnector />
      <Titles
        width={[370, 1]}
        flexDirection="column"
        alignItems="center"
        zIndex="1"
        gap="20px"
        px={[25, 0, 0]}
        mb={[15, 0]}
      >
        <Title tabIndex={0}>{parse(title)}</Title>
        <Subtitle tabIndex={0}>{parse(subtitle)}</Subtitle>
      </Titles>
      <LottieSection />
      <BottomConnector />
    </Section>
  );
};

export default React.memo(LowMilage);
